import { Card, Container, Table, TableBody, TableContainer } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { useForm } from 'react-hook-form';
import { useLocation, useParams } from 'react-router';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs/custom-breadcrumbs';
import FormProvider from 'src/components/hook-form/form-provider';
import Scrollbar from 'src/components/scrollbar';
import {
  TableHeadCustom,
  TablePaginationCustom,
  TableSkeleton,
  useTable,
} from 'src/components/table';
import { SOUND_TABLE_HEAD } from 'src/constants/tables-heads';
import {
  useDeleteSoundMutation,
  useSoundsByMatnSectionIdQuery
} from 'src/graphql';
import { useBoolean } from 'src/hooks/use-boolean';
import { useLocales } from 'src/locales';
import { paths } from 'src/routes/paths';
import AddNewCategoryDialog from 'src/sections/dialogs/add/AddNewCategoryDialog';
import { MatnSectionDiffWordStateType } from 'src/types/matn-section-diff-words';
import { getLocalizedValue } from 'src/utils/get-localized-value';
import MatnSectionSoundsRow from './matn-section-sounds-row';

const MatnSectionSounds = () => {
  // #region States and hooks
  const table = useTable({ defaultRowsPerPage: 10, defaultCurrentPage: 0 });
  const addDialog = useBoolean();
  const queryClient = useQueryClient();
  const { categoryId, levelId, matnId, matnSectionId } = useParams();
  const { state }: { state: MatnSectionDiffWordStateType | undefined } = useLocation();
  const methods = useForm();
  const { t } = useLocales();

  const categoryName = state ? getLocalizedValue(state.category?.name) : 'Levels';
  const levelName = state ? getLocalizedValue(state.level.name) : 'Mutoon';
  const matnName = state ? getLocalizedValue(state.matn.name) : 'Matn';
  const matnSectionName = state ? getLocalizedValue(state.matn.name) : 'Matn section';
  // #endregion States and hooks

  // #region Services
  const { data, isLoading } = useSoundsByMatnSectionIdQuery({
    offset: table.offset,
    limit: table.rowsPerPage,
    matnSectionId,
  });

  const { mutate, isLoading: isDeleting } = useDeleteSoundMutation({
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['SoundsByMatnSectionId'] }),
  });
  // #endregion Services

  // #region Handlers
  const handleDeleteMatnSection = (soundId: string) => {
    mutate({ soundId });
  };
  // #endregion Handlers

  return (
    <Container>
      <Helmet>
        <title>{t('tables.matnSections.sounds')}</title>
      </Helmet>
      <CustomBreadcrumbs
        links={[
          {
            name: t('breadcrumbs.mutoon'),
          },
          {
            name: t('breadcrumbs.categories'),
            href: paths.dashboard.contentManagement.mutoon.categories,
          },
          {
            name: categoryName,
            href: `${paths.dashboard.contentManagement.mutoon.categories}/${categoryId}`,
          },
          {
            name: levelName,
            href: `${paths.dashboard.contentManagement.mutoon.categories}/${categoryId}/${levelId}`,
          },
          {
            name: matnName,
            href: `${paths.dashboard.contentManagement.mutoon.categories}/${categoryId}/${levelId}/${matnId}`,
          },
          {
            name: matnSectionName,
          },
        ]}
      />
      <Card>
        <FormProvider methods={methods}>
          <TableContainer>
            <Scrollbar>
              <Table>
                <TableHeadCustom headLabel={SOUND_TABLE_HEAD(t)} />
                <TableBody>
                  {isLoading || isDeleting ? (
                    [...Array(table.rowsPerPage)].map((i, index) => (
                      <TableSkeleton key={index} sx={{ height: 80 }} />
                    ))
                  ) : (
                    <>
                      {data?.sounds?.map((row) => (
                        <MatnSectionSoundsRow
                          key={row.soundId}
                          row={row}
                          deleteHandler={handleDeleteMatnSection}
                        />
                      ))}
                    </>
                  )}
                </TableBody>
              </Table>
            </Scrollbar>
          </TableContainer>
          {data && (
            <TablePaginationCustom
              count={+data.sounds_aggregate.aggregate?.count!}
              page={+table.page}
              rowsPerPage={table.rowsPerPage}
              onPageChange={table.onChangePage}
              onRowsPerPageChange={table.onChangeRowsPerPage}
            />
          )}
        </FormProvider>
        <AddNewCategoryDialog open={addDialog.value} onClose={addDialog.onFalse} />
      </Card>
    </Container>
  );
};

export default MatnSectionSounds;
